import { styled, Typography } from '@mui/material';
import MUITypography from '@mui/material/Typography';
import type { PartNode } from '@partful/shared-types';
import { useTenant } from '@providers/Tenant';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Feature } from 'src/Feature';
import { hasPrice } from 'src/services/isPartBuyable';
import { AddToCartButton } from '../AddToCartButton';
import { SetQuantity } from '../SetQuantity';
import { PriceSection } from './PriceSection';

interface BuyPartsProps {
  currentPart: PartNode;
  defaultBuyableQuantity?: string;
}
export function BuyPartsSection({ currentPart, defaultBuyableQuantity }: BuyPartsProps) {
  const [quantity, setQuantity] = useState<number | ''>(parseInt(defaultBuyableQuantity || '1'));
  const isBuyable = hasPrice(currentPart); // undefined price means part is not buyable

  const { t } = useTranslation('partsCatalogue');

  const tenant = useTenant();
  const configManagement = tenant.enabledFeatures.includes('configuration-management');

  useEffect(() => {
    setQuantity(parseInt(defaultBuyableQuantity || '1'));
  }, [currentPart, defaultBuyableQuantity]);

  const cartQuantity = quantity === '' ? /* istanbul ignore next */ 1 : quantity;
  const addToCartButtonDisabled = quantity === 0 || quantity === '';

  if (!isBuyable) {
    if (configManagement && currentPart.notAvailableToOrder === true) {
      return <MUITypography variant="subtitle1">{t('notAvailableToOrderIndividually')}</MUITypography>;
    }

    return null;
  }

  return (
    <>
      <Feature flag="shopping-cart">
        <>
          <QuantityText>{t('quantity')}</QuantityText>
          <SetQuantityContainer>
            <SetQuantity quantity={quantity} setQuantity={setQuantity} allowZero />
          </SetQuantityContainer>
        </>
      </Feature>
      {/* Always render the PriceSection if there's a price, regardless of shopping-cart feature */}
      {currentPart.price && <PriceSection price={currentPart.price} />}
      <Feature flag="shopping-cart">
        <AddToCartButton quantity={cartQuantity} part={currentPart} disabled={addToCartButtonDisabled} />
      </Feature>
    </>
  );
}
const QuantityText = styled(Typography)`
  margin-bottom: 0.3em;
`;

const SetQuantityContainer = styled('div')`
  margin-bottom: 2em;
`;
